import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, UserCog, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MultiEmailSelector } from "./multi-email-selector";
import { useCustomerContactsList, useBulkCustomerContactsList } from "@/hooks/useCustomerContacts";
import { useLogPromisePhoneCall } from "@/hooks/useLogPromisePhoneCall";
import { useLogDisputePhoneCall } from "@/hooks/useLogDisputePhoneCall";
import { useTemplatesList, useTemplateById } from "@/hooks/useTemplatesData";
import { useLogPromiseEmail } from "@/hooks/useLogPromiseEmail";
import { useLogDisputeEmail } from "@/hooks/useLogDisputeEmail";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { VariableExtension } from "@/lib/tiptap-variable-extension";
import type { Editor } from "@tiptap/react";

// Form value types for modal callbacks
export interface RecordPromiseFormValues {
  promiseDate: Date | undefined;
  amount: number;
  method: string;
  notes: string;
  sendConfirmation: boolean;
}

export interface MarkPromiseFulfilledFormValues {
  paymentDate: Date;
  amountPaid: number;
  paymentMethod: string;
  reference: string;
  notes: string;
  promiseFulfilled: boolean;
}

export interface MarkPromiseBrokenFormValues {
  reason: string;
  notes: string;
  escalate: boolean;
  scheduleFollowUp: boolean;
  followUpDate: Date | undefined;
  promiseBroken: boolean;
}

interface LogCallModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly customerPhone?: string;
  readonly promiseId?: string;
  readonly disputeId?: string;
}

export function LogCallModal({ open, onOpenChange, customerName, customerPhone, promiseId, disputeId }: LogCallModalProps) {
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [followUpDate, setFollowUpDate] = useState<Date>();
  const [phoneNumber, setPhoneNumber] = useState("");
  const logPromisePhoneCallMutation = useLogPromisePhoneCall();
  const logDisputePhoneCallMutation = useLogDisputePhoneCall();

  // Initialize phone number when modal opens or customerPhone changes
  useEffect(() => {
    if (open) {
      setPhoneNumber(customerPhone || "");
    }
  }, [open, customerPhone]);

  const handleSave = () => {
    const callbackOptions = {
      onSuccess: () => {
        toast.success("Call logged successfully");
        onOpenChange(false);
        setDuration("");
        setNotes("");
        setFollowUpDate(undefined);
        setPhoneNumber("");
      },
      onError: () => {
        toast.error("Failed to log call");
      },
    };

    if (disputeId) {
      logDisputePhoneCallMutation.mutate(
        {
          disputeId,
          phoneNumber: phoneNumber || undefined,
          durationMinutes: duration ? parseInt(duration, 10) : undefined,
          notes: notes || undefined,
          followUpDate: followUpDate ? followUpDate.toISOString() : undefined,
        },
        callbackOptions
      );
    } else if (promiseId) {
      logPromisePhoneCallMutation.mutate(
        {
          promiseId,
          phoneNumber: phoneNumber || undefined,
          durationMinutes: duration ? parseInt(duration, 10) : undefined,
          notes: notes || undefined,
          followUpDate: followUpDate ? followUpDate.toISOString() : undefined,
        },
        callbackOptions
      );
    } else {
      toast.success("Call logged successfully");
      onOpenChange(false);
      setDuration("");
      setNotes("");
      setFollowUpDate(undefined);
      setPhoneNumber("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Log Call</DialogTitle>
          <DialogDescription>Record details of your call with {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input 
              id="phone" 
              value={phoneNumber} 
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number" 
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="duration">Duration (minutes)</Label>
            <Input
              id="duration"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="5"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter call notes..."
              rows={4}
            />
          </div>
          <div className="grid gap-2">
            <Label>Follow-up Date (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("justify-start text-left font-normal", !followUpDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {followUpDate ? format(followUpDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={followUpDate} onSelect={setFollowUpDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Call Log</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Strip HTML tags for plain text display
function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// Convert plain text {{variable}} patterns to TipTap variable nodes in HTML
// First normalizes by stripping any existing variable nodes to avoid double-conversion
const convertVariablesToNodes = (html: string): string => {
  // Step 1: Strip any existing <span data-variable> nodes back to plain text {{var}}
  const normalized = html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1');
  // Step 2: Convert all {{var}} patterns to variable node spans
  const variableRegex = /\{\{([^}]+)\}\}/g;
  return normalized.replace(variableRegex, (match) => {
    return `<span data-variable="${match}" class="variable-node"></span>`;
  });
};

// Convert variable nodes back to plain text for subject extraction
const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1')
    .replace(/<\/?p>/g, '')
    .trim();
};

export type EmailSourceType = "promise" | "collection" | "customer" | "dispute";

interface SendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerEmail: string;
  customerId?: string;
  sourceType?: EmailSourceType;
  sourceId?: string;
  isBulkEmail?: boolean;
  bulkCustomerCount?: number;
  bulkCustomerIds?: string[]; // Array of customer IDs for bulk emails
}

export function SendEmailModal({ open, onOpenChange, customerName, customerEmail, customerId, sourceType, sourceId, isBulkEmail = false, bulkCustomerCount = 1, bulkCustomerIds = [] }: Readonly<SendEmailModalProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <SendEmailModalContent
          onOpenChange={onOpenChange}
          customerName={customerName}
          customerEmail={customerEmail}
          customerId={customerId}
          sourceType={sourceType}
          sourceId={sourceId}
          isBulkEmail={isBulkEmail}
          bulkCustomerCount={bulkCustomerCount}
          bulkCustomerIds={bulkCustomerIds}
        />
      )}
    </Dialog>
  );
}

function SendEmailModalContent({ onOpenChange, customerName, customerEmail, customerId, sourceType, sourceId, isBulkEmail = false, bulkCustomerCount = 1, bulkCustomerIds = [] }: Readonly<Omit<SendEmailModalProps, 'open'>>) {
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subjectHtml, setSubjectHtml] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [template, setTemplate] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [attachInvoices, setAttachInvoices] = useState(false);

  // Track original template values for modification detection
  const originalTemplateSubject = useRef<string>("");
  const originalTemplateBody = useRef<string>("");

  // Refs for TipTap editors
  const bodyEditorRef = useRef<Editor | null>(null);

  // Email logging mutations
  const logPromiseEmailMutation = useLogPromiseEmail();
  const logDisputeEmailMutation = useLogDisputeEmail();

  // Fetch templates from API - hooks only run when modal is open
  const { data: templatesData } = useTemplatesList();
  const templates = Array.isArray(templatesData) ? templatesData : [];
  const { data: templateDetail } = useTemplateById(selectedTemplateId);

  // Fetch customer contacts - for single customer or bulk customers
  const { data: singleCustomerContacts = [] } = useCustomerContactsList(!isBulkEmail && customerId ? customerId : "0");
  const { data: bulkContacts = [] } = useBulkCustomerContactsList(isBulkEmail ? bulkCustomerIds : []);
  
  // Use appropriate contacts based on mode
  const contacts = isBulkEmail ? bulkContacts : singleCustomerContacts;

  // TipTap subject editor (single-line, with variable pills)
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Payment Reminder',
      }),
      VariableExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-3 min-h-[44px]',
      },
    },
    onUpdate: ({ editor }) => {
      setSubjectHtml(editor.getHTML());
    },
  });

  // Callback when body RichTextEditor is ready
  const handleBodyEditorReady = useCallback((editor: Editor) => {
    bodyEditorRef.current = editor;
  }, []);

  // Initialize toEmails once when component mounts or contacts first load
  const emailsInitialized = useRef(false);
  useEffect(() => {
    if (emailsInitialized.current) return;
    if (customerEmail) {
      setToEmails([customerEmail]);
      emailsInitialized.current = true;
    } else if (contacts.length > 0) {
      const primaryContact = contacts.find(x => x.isPrimary) || contacts[0];
      if (primaryContact?.email) {
        setToEmails([primaryContact.email]);
        emailsInitialized.current = true;
      }
    }
  }, [customerEmail, contacts]);

  // Populate subject and body when template detail is fetched
  useEffect(() => {
    if (templateDetail && selectedTemplateId) {
      // Convert {{variable}} patterns to pill nodes for both subject and body
      // convertVariablesToNodes normalizes first (strips existing nodes) to avoid double-conversion
      const rawSubject = templateDetail.subject ?? "";
      const convertedSubject = convertVariablesToNodes(rawSubject);
      const rawBody = templateDetail.content ?? "";
      const convertedBody = convertVariablesToNodes(rawBody);

      // Set subject: always update state (subject editor syncs via its own onUpdate)
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent(convertedSubject);
        originalTemplateSubject.current = subjectEditor.getHTML();
      }
      setSubjectHtml(convertedSubject);

      // Set body content in editor and sync state with the editor's rendered HTML.
      // We must call setBodyHtml with the editor's getHTML() output (not the raw input)
      // so that the RichTextEditor's content prop matches the editor's actual HTML.
      // This prevents the RichTextEditor's internal useEffect from re-setting the editor
      // (which uses { emitUpdate: false } and would desync bodyHtml from the editor).
      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent(convertedBody);
        const renderedBodyHtml = bodyEditorRef.current.getHTML();
        originalTemplateBody.current = renderedBodyHtml;
        setBodyHtml(renderedBodyHtml);
      } else {
        // Editor not ready yet — set via prop; RichTextEditor's useEffect will sync to editor
        setBodyHtml(convertedBody);
        originalTemplateBody.current = convertedBody;
      }
    }
  }, [templateDetail, selectedTemplateId, subjectEditor]);

  // Handle template selection
  const handleTemplateChange = (selectedTemplate: string) => {
    setTemplate(selectedTemplate);

    if (selectedTemplate === "none") {
      setSelectedTemplateId(null);
      setSubjectHtml("");
      originalTemplateSubject.current = "";
      originalTemplateBody.current = "";

      // Clear TipTap editors
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent("");
      }
      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent("");
      }
      setBodyHtml("");
    } else {
      setSelectedTemplateId(selectedTemplate);
    }
  };

  const handleSend = () => {
    if (toEmails.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }

    // Extract plain text subject (with variable placeholders restored)
    const currentSubjectHtml = subjectEditor?.getHTML() || subjectHtml;
    const subjectText = convertVariableNodesToText(currentSubjectHtml);

    if (!subjectText.trim()) {
      toast.error("Please enter a subject");
      return;
    }

    // Check body content using editor's isEmpty for reliable detection
    const bodyEditor = bodyEditorRef.current;
    const isBodyEmpty = bodyEditor && !bodyEditor.isDestroyed
      ? bodyEditor.isEmpty
      : !stripHtml(bodyHtml).trim();

    if (isBodyEmpty) {
      toast.error("Please enter a message");
      return;
    }

    // Get body HTML from state (primary) or editor (fallback)
    // bodyHtml state is reliably kept in sync via RichTextEditor's onUpdate → onChange callback
    const currentBodyHtml = bodyHtml || bodyEditor?.getHTML() || '';

    const isSubjectModified = selectedTemplateId
      ? currentSubjectHtml !== originalTemplateSubject.current
      : false;
    const isBodyModified = selectedTemplateId
      ? currentBodyHtml !== originalTemplateBody.current
      : false;

    const selectedTemplateName = selectedTemplateId
      ? templates.find(x => x.id === selectedTemplateId)?.name
      : undefined;

    const emailPayload = {
      sourceId: sourceId ?? "",
      toRecipients: toEmails,
      ccRecipients: ccEmails.length > 0 ? ccEmails : undefined,
      subject: subjectText,
      htmlBody: currentBodyHtml,
      templateId: selectedTemplateId ? parseInt(selectedTemplateId, 10) : undefined,
      templateName: selectedTemplateName,
      isSubjectModified,
      isBodyModified,
      attachOutstandingInvoices: attachInvoices,
    };

    const mutationCallbacks = {
      onSuccess: () => {
        toast.success(`Email sent successfully to ${toEmails.length} recipient(s)`);
        onOpenChange(false);
      },
      onError: () => {
        toast.error("Failed to send email");
      },
    };

    if (sourceType === "promise" && sourceId) {
      logPromiseEmailMutation.mutate(emailPayload, mutationCallbacks);
    } else if (sourceType === "dispute" && sourceId) {
      logDisputeEmailMutation.mutate(emailPayload, mutationCallbacks);
    } else {
      toast.success(`Email sent successfully to ${toEmails.length} recipient(s)`);
      onOpenChange(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
      <DialogHeader>
        <DialogTitle>Send Email</DialogTitle>
        <DialogDescription>
          {isBulkEmail 
            ? `Send an email to ${bulkCustomerCount} customers` 
            : `Send an email to ${customerName}`}
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-2 overflow-y-auto flex-1 min-h-0">
        <MultiEmailSelector
          label="To"
          selectedEmails={toEmails}
          onEmailsChange={setToEmails}
          contacts={contacts}
          defaultEmail={isBulkEmail ? undefined : customerEmail}
          required={true}
          placeholder={isBulkEmail ? "Select recipients from customer contacts..." : "Add recipient..."}
          helperText={isBulkEmail ? `Select from ${bulkCustomerCount} customers' contacts` : undefined}
        />
        <MultiEmailSelector
          label="CC (Optional)"
          selectedEmails={ccEmails}
          onEmailsChange={setCcEmails}
          contacts={contacts}
          required={false}
          placeholder="Add CC..."
        />
        <div className="grid gap-2">
          <Label htmlFor="template">Template (Optional)</Label>
          <Select value={template} onValueChange={handleTemplateChange}>
            <SelectTrigger id="template">
              <SelectValue placeholder="Select a template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Subject</Label>
          {subjectEditor ? (
            <div className="border rounded-md">
              <EditorContent 
                editor={subjectEditor} 
                className="prose prose-sm max-w-none [&_.ProseMirror]:p-3 [&_.ProseMirror]:min-h-[44px] [&_.ProseMirror]:focus:outline-none"
              />
            </div>
          ) : (
            <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">
              Loading editor...
            </div>
          )}
        </div>
        <div className="grid gap-2">
          <Label>Message</Label>
          <RichTextEditor
            content={bodyHtml}
            onChange={setBodyHtml}
            onEditorReady={handleBodyEditorReady}
            placeholder="Enter your message..."
          />
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox id="attach-invoices" checked={attachInvoices} onCheckedChange={(checked) => setAttachInvoices(checked === true)} />
          <Label htmlFor="attach-invoices" className="text-sm font-normal">
            Attach outstanding invoices
          </Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleSend}>Send Email</Button>
      </DialogFooter>
    </DialogContent>
  );
}

interface RecordPromiseModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly caseId?: string;
  readonly caseAmount?: number;
  readonly onSave?: (data: RecordPromiseFormValues) => void;
}

export function RecordPromiseModal({ open, onOpenChange, customerName, caseId, caseAmount, onSave }: RecordPromiseModalProps) {
  const [promiseDate, setPromiseDate] = useState<Date>();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [sendConfirmation, setSendConfirmation] = useState(true);

  // Set default amount when modal opens or caseAmount changes
  useEffect(() => {
    if (caseAmount !== undefined && open) {
      setAmount(caseAmount.toString());
    }
  }, [caseAmount, open]);

  const handleSave = () => {
    const data = {
      promiseDate,
      amount: parseFloat(amount),
      method,
      notes,
      sendConfirmation,
    };
    
    if (onSave) {
      onSave(data);
    }
    
    toast.success("Promise to pay recorded successfully");
    onOpenChange(false);
    // Reset form
    setPromiseDate(undefined);
    setAmount("");
    setMethod("");
    setNotes("");
    setSendConfirmation(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Promise to Pay</DialogTitle>
          <DialogDescription>Record a payment commitment from {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer-promise">Customer</Label>
            <Input id="customer-promise" value={caseId ? `${customerName} (${caseId})` : customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label>Promise Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("justify-start text-left font-normal", !promiseDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {promiseDate ? format(promiseDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={promiseDate} onSelect={setPromiseDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="amount">Promised Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="method">Confirmation Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="portal">Customer Portal</SelectItem>
                <SelectItem value="in-person">In Person</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-promise">Notes</Label>
            <Textarea
              id="notes-promise"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="send-confirmation" checked={sendConfirmation} onCheckedChange={(checked) => setSendConfirmation(checked as boolean)} />
            <Label htmlFor="send-confirmation" className="text-sm font-normal">
              Send confirmation email to customer
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Promise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MarkPromiseFulfilledModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly promiseAmount: number;
  readonly promiseDate: Date;
  readonly onSave?: (data: MarkPromiseFulfilledFormValues) => void;
}

export function MarkPromiseFulfilledModal({ 
  open, 
  onOpenChange, 
  customerName, 
  promiseAmount,
  promiseDate,
  onSave 
}: MarkPromiseFulfilledModalProps) {
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [amountPaid, setAmountPaid] = useState(promiseAmount.toString());
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    const data = {
      paymentDate,
      amountPaid: parseFloat(amountPaid),
      paymentMethod,
      reference,
      notes,
      promiseFulfilled: true,
    };
    
    if (onSave) {
      onSave(data);
    }
    
    toast.success("Promise marked as fulfilled");
    onOpenChange(false);
    // Reset form
    setPaymentDate(new Date());
    setAmountPaid(promiseAmount.toString());
    setPaymentMethod("");
    setReference("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark Promise as Fulfilled</DialogTitle>
          <DialogDescription>
            Record payment received from {customerName}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Promise Details</Label>
            <div className="text-sm text-muted-foreground">
              <div>Promised Amount: ${promiseAmount.toFixed(2)}</div>
              <div>Promise Date: {format(promiseDate, "PPP")}</div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Payment Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("justify-start text-left font-normal")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(paymentDate, "PPP")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar 
                  mode="single" 
                  selected={paymentDate} 
                  onSelect={(date) => date && setPaymentDate(date)} 
                  initialFocus 
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="amount-paid">Amount Paid</Label>
            <Input
              id="amount-paid"
              type="number"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-method">Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="payment-method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank-transfer">Bank Transfer</SelectItem>
                <SelectItem value="credit-card">Credit Card</SelectItem>
                <SelectItem value="debit-card">Debit Card</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reference">Payment Reference</Label>
            <Input
              id="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Transaction ID, check number, etc."
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-fulfilled">Notes</Label>
            <Textarea
              id="notes-fulfilled"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Confirm Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MarkPromiseBrokenModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly promiseAmount: number;
  readonly promiseDate: Date;
  readonly onSave?: (data: MarkPromiseBrokenFormValues) => void;
}

export function MarkPromiseBrokenModal({ 
  open, 
  onOpenChange, 
  customerName, 
  promiseAmount,
  promiseDate,
  onSave 
}: MarkPromiseBrokenModalProps) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [escalate, setEscalate] = useState(false);
  const [scheduleFollowUp, setScheduleFollowUp] = useState(true);
  const [followUpDate, setFollowUpDate] = useState<Date>();

  const handleSave = () => {
    const data = {
      reason,
      notes,
      escalate,
      scheduleFollowUp,
      followUpDate,
      promiseBroken: true,
    };
    
    if (onSave) {
      onSave(data);
    }
    
    toast.warning("Promise marked as broken");
    onOpenChange(false);
    // Reset form
    setReason("");
    setNotes("");
    setEscalate(false);
    setScheduleFollowUp(true);
    setFollowUpDate(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark Promise as Broken</DialogTitle>
          <DialogDescription>
            Record that {customerName} did not fulfill their payment commitment
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Promise Details</Label>
            <div className="text-sm text-muted-foreground">
              <div>Promised Amount: ${promiseAmount.toFixed(2)}</div>
              <div>Promise Date: {format(promiseDate, "PPP")}</div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-contact">Unable to Contact</SelectItem>
                <SelectItem value="no-payment">No Payment Received</SelectItem>
                <SelectItem value="partial-payment">Partial Payment Only</SelectItem>
                <SelectItem value="customer-dispute">Customer Disputes Amount</SelectItem>
                <SelectItem value="financial-difficulty">Financial Difficulty</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-broken">Notes</Label>
            <Textarea
              id="notes-broken"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details about why the promise was broken..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="escalate" 
              checked={escalate} 
              onCheckedChange={(checked) => setEscalate(checked as boolean)} 
            />
            <Label htmlFor="escalate" className="text-sm font-normal">
              Escalate case to supervisor
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="schedule-follow-up" 
              checked={scheduleFollowUp} 
              onCheckedChange={(checked) => setScheduleFollowUp(checked as boolean)} 
            />
            <Label htmlFor="schedule-follow-up" className="text-sm font-normal">
              Schedule follow-up action
            </Label>
          </div>
          {scheduleFollowUp && (
            <div className="grid gap-2">
              <Label>Follow-up Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("justify-start text-left font-normal", !followUpDate && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {followUpDate ? format(followUpDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar 
                    mode="single" 
                    selected={followUpDate} 
                    onSelect={setFollowUpDate} 
                    initialFocus 
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSave}>Mark as Broken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReassignUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onSave?: (userId: string, notes: string) => void;
  users?: { id: string; name: string; avatar: string }[];
  isLoadingUsers?: boolean;
}

export function ReassignUserModal({ open, onOpenChange, selectedCount, onSave, users = [], isLoadingUsers = false }: Readonly<ReassignUserModalProps>) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    if (!selectedUserId) {
      toast.error("Please select a user to reassign to");
      return;
    }

    if (onSave) {
      onSave(selectedUserId, notes);
    }

    const selectedUser = users.find(u => u.id === selectedUserId);
    toast.success(`Reassigned ${selectedCount} promise(s) to ${selectedUser?.name}`);
    onOpenChange(false);
    // Reset form
    setSelectedUserId("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Reassign User
          </DialogTitle>
          <DialogDescription>
            Reassign {selectedCount} selected promise{selectedCount > 1 ? 's' : ''} to another user
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="user">Select User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={isLoadingUsers}>
              <SelectTrigger id="user">
                <SelectValue placeholder={isLoadingUsers ? "Loading users..." : "Choose a user"} />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback>{user.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <span>{user.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reassign-notes">Notes (Optional)</Label>
            <Textarea
              id="reassign-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this reassignment..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface UpdateStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onSave?: (status: string, notes: string) => void;
}

export function UpdateStatusModal({ open, onOpenChange, selectedCount, onSave }: Readonly<UpdateStatusModalProps>) {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    if (!status) {
      toast.error("Please select a status");
      return;
    }

    if (onSave) {
      onSave(status, notes);
    }

    toast.success(`Updated status for ${selectedCount} promise(s) to ${status}`);
    onOpenChange(false);
    // Reset form
    setStatus("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Update Status
          </DialogTitle>
          <DialogDescription>
            Update the status for {selectedCount} selected promise{selectedCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="status">New Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Broken">Broken</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="status-notes">Notes (Optional)</Label>
            <Textarea
              id="status-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status change..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Update Status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
